import { writable, type Writable } from 'svelte/store';
import { isRoomCode, type ClientMessage, type ErrorCode, type ServerMessage } from '@banger/shared';
import { applyView, view } from './view';
import { clearHost } from './session';
import { checkForNewBuild } from './version';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export const connectionStatus: Writable<ConnectionStatus> = writable('idle');
export const lastError: Writable<{ code: ErrorCode; message: string } | null> = writable(null);

/**
 * Errors after which reconnecting can never succeed (the server does not remember kicks).
 * REPLACED: another display tab took over; reconnecting would kick it back, forever.
 */
const TERMINAL: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'KICKED', 'ROOM_NOT_FOUND', 'BAD_TOKEN', 'ROOM_FULL', 'NAME_TAKEN', 'BAD_NAME', 'REPLACED',
]);

const BASE_RECONNECT_DELAY_MS = 500;
const MAX_RECONNECT_DELAY_MS = 8_000;
// iOS Safari silently suspends sockets: readyState stays OPEN and send() succeeds
// into a dead connection. Liveness is judged by what ARRIVES. The server answers
// pings and broadcasts a view every 5s, so silence past STALE_MS means dead.
const HEARTBEAT_INTERVAL_MS = 8_000;
const STALE_MS = 20_000;
const MAX_NEVER_OPENED = 5;

let ws: WebSocket | null = null;
let hello: ClientMessage | null = null;
let roomCode: string | null = null;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let lastInboundAt = 0;
let visibilityInstalled = false;
let neverOpened = 0;
/** A socket has opened since begin(): the room is real, so failed opens are just network trouble. */
let everOpened = false;

function backoffDelay(attempt: number): number {
  return Math.min(BASE_RECONNECT_DELAY_MS * 2 ** attempt, MAX_RECONNECT_DELAY_MS) + Math.random() * 200;
}

function buildUrl(code: string): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/api/rooms/${code}/ws`;
}

function fail(code: ErrorCode, message: string): void {
  teardown();
  view.set(null);
  lastError.set({ code, message });
  connectionStatus.set('disconnected');
}

function begin(code: string, msg: ClientMessage): void {
  teardown();
  if (!isRoomCode(code)) {
    fail('ROOM_NOT_FOUND', 'That is not a valid room code.');
    return;
  }
  roomCode = code;
  neverOpened = 0;
  everOpened = false;
  hello = msg;
  reconnectAttempts = 0;
  lastError.set(null);
  openSocket();
}

export function connectDisplay(code: string, hostToken: string): void {
  const c = code.toUpperCase();
  begin(c, { type: 'hello_display', code: c, hostToken });
}

export function connectPlayer(code: string, playerId: string, name: string, emoji: string): void {
  const c = code.toUpperCase();
  begin(c, { type: 'hello_player', code: c, playerId, name, emoji });
}

export function send(msg: ClientMessage): void {
  if (!ws || ws.readyState !== 1) return;
  try { ws.send(JSON.stringify(msg)); } catch { /* socket gone; watchdog handles it */ }
}

export function disconnect(): void {
  teardown();
  connectionStatus.set('idle');
}

function teardown(): void {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  stopHeartbeat();
  roomCode = null;
  hello = null;
  const old = ws;
  ws = null;
  if (old) {
    old.onclose = null;
    old.onmessage = null;
    try { old.close(); } catch { /* */ }
  }
}

function openSocket(): void {
  if (!roomCode || !hello) return;
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (ws) {
    ws.onclose = null;
    try { ws.close(); } catch { /* */ }
  }
  connectionStatus.set(reconnectAttempts === 0 ? 'connecting' : 'reconnecting');
  installVisibilityHandler();

  const sock = new WebSocket(buildUrl(roomCode));
  ws = sock;
  let opened = false;

  sock.onopen = () => {
    if (ws !== sock || !hello) return;
    opened = true;
    everOpened = true;
    connectionStatus.set('connected');
    reconnectAttempts = 0;
    neverOpened = 0;
    lastInboundAt = Date.now();
    sock.send(JSON.stringify(hello));
    startHeartbeat();
    checkForNewBuild();
  };

  sock.onmessage = (event) => {
    if (ws !== sock) return;
    lastInboundAt = Date.now();
    let msg: ServerMessage;
    try {
      msg = JSON.parse(typeof event.data === 'string' ? event.data : '') as ServerMessage;
    } catch {
      return;
    }
    if (msg.type === 'error') {
      lastError.set({ code: msg.code, message: msg.message });
      if (TERMINAL.has(msg.code)) {
        // Stop for good: no reconnect, no further handling of this socket.
        const wasDisplay = hello?.type === 'hello_display';
        if (wasDisplay && (msg.code === 'ROOM_NOT_FOUND' || msg.code === 'BAD_TOKEN')) clearHost();
        fail(msg.code, msg.message);
        try { sock.close(); } catch { /* */ }
      }
    } else if (msg.type === 'view') {
      lastError.set(null);
      applyView(msg.view);
    }
  };

  sock.onclose = () => {
    if (ws !== sock) return;
    stopHeartbeat();
    // Only a session that has never connected (bad or unreachable code) gives up;
    // once in, keep retrying: the server itself reports a room that really ended.
    if (!opened && !everOpened) {
      neverOpened += 1;
      if (neverOpened >= MAX_NEVER_OPENED) {
        fail('ROOM_NOT_FOUND', 'Could not reach that room.');
        return;
      }
    }
    connectionStatus.set('disconnected');
    if (roomCode && hello) scheduleReconnect();
  };

  sock.onerror = () => { /* onclose follows */ };
}

function installVisibilityHandler(): void {
  if (visibilityInstalled || typeof document === 'undefined') return;
  visibilityInstalled = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    checkForNewBuild();
    if (!roomCode || !hello) return;
    if (!ws || ws.readyState !== 1) { reconnectAttempts = 0; openSocket(); return; }
    if (lastInboundAt > 0 && Date.now() - lastInboundAt > STALE_MS) { forceReconnect(); return; }
    try { ws.send(JSON.stringify({ type: 'ping' })); } catch { reconnectAttempts = 0; openSocket(); }
  });
}

function startHeartbeat(): void {
  stopHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (!ws || ws.readyState !== 1) return;
    if (lastInboundAt > 0 && Date.now() - lastInboundAt > STALE_MS) { forceReconnect(); return; }
    try { ws.send(JSON.stringify({ type: 'ping' })); } catch { forceReconnect(); }
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat(): void {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
}

function forceReconnect(): void {
  stopHeartbeat();
  connectionStatus.set('reconnecting');
  const dead = ws;
  ws = null;
  lastInboundAt = 0;
  if (dead) {
    dead.onclose = null;
    try { dead.close(); } catch { /* */ }
  }
  reconnectAttempts = 0;
  openSocket();
}

function scheduleReconnect(): void {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  const delay = backoffDelay(reconnectAttempts);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => { reconnectTimer = null; openSocket(); }, delay);
}
